import { useEffect, useId, useState, type ReactNode } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { cn } from "../../../app/components/primitives/utils";

// The production and compliance option groups (proofing, certification, ...)
// behind one disclosure, closed by default, its closed state summarising the
// current choices. Never hides a problem: when a group inside has a selection
// that can't be ordered the disclosure opens itself and, closed or not, says so.
export function MoreOptions({
  summary,
  attentionKeys,
  children,
}: {
  /** "Proofing: PDF proof · Certification: None", shown while closed. */
  summary: string;
  /** Keys of groups inside whose selection needs the customer's attention. */
  attentionKeys: readonly string[];
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const needsAttention = attentionKeys.length > 0;

  // Open on a group newly needing attention, but let the customer close it
  // again afterwards (the notice below stays while the problem does).
  const attentionSignature = attentionKeys.join("|");
  useEffect(() => {
    if (attentionSignature) setIsOpen(true);
  }, [attentionSignature]);

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-start justify-between gap-3 text-left focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <span className="min-w-0 space-y-0.5">
          <span className="block text-sm font-medium">More options</span>
          {!isOpen && (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {summary}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn(
            "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
            isOpen && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      {needsAttention && (
        <p
          role="alert"
          className="flex items-center gap-1.5 text-xs text-destructive"
        >
          <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
          One of these needs a different choice.
        </p>
      )}
      <div id={panelId} hidden={!isOpen} className="space-y-5">
        {isOpen && children}
      </div>
    </div>
  );
}
